'use client';

import Link from 'next/link';
import { useEffect, useRef, useState, type PointerEvent } from 'react';
import { trackSectionCta } from '@/components/GoogleAnalytics';

const CDN_BASE = (process.env.NEXT_PUBLIC_IMAGE_CDN_URL || '').replace(/\/$/, '');

const REELS = [
  { file: 'Video-17261.mp4', name: 'Sanjay Kakade', handle: '@kakade.sanjay' },
  { file: 'Video-20569.mp4', name: 'Tapasya & Vinit', handle: '@smilewithtavi27' },
  { file: 'Video-24306.mp4', name: 'Shrutika Kulkarni & Prasad Zunjar', handle: '@prasad.shrutika' },
  { file: 'Video-37156.mp4', name: 'Lina & Sanket', handle: '@linasanket' },
  { file: 'Video-39218.mp4', name: 'Shrutika & Vipul Jaybhaye', handle: '@littlethings_by_shruvi' },
  { file: 'Video-55781.mp4', name: 'Vishal & Shreya', handle: '@shreyavishal.tales' },
  { file: 'Video-74056.mp4', name: 'Neha Dixit', handle: '@neha_dixittttt' },
  { file: 'Video-76807.mp4', name: 'Aditi & Omkar', handle: '@adiom_lovetales' },
  { file: 'Video-78550.mp4', name: 'Komal and Vinod', handle: '@_rooted.with.love_' },
  { file: 'Video-86278.mp4', name: 'Rudrani Joshi & Sumit Nagpure', handle: '@rudrani.sumit' },
  { file: 'Video-97378.mp4', name: 'Giri Gajanan', handle: '@giri_gajanan_official' },
] as const;

function reelSrc(file: string) {
  return `${CDN_BASE}/assets/video/reels/${file}`;
}

function ReelCard({ src, name, handle }: { src?: string; name: string; handle: string }) {
  const cardRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [near, setNear] = useState(false);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (!src) return;
    const card = cardRef.current;
    if (!card) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setNear(true);
        observer.disconnect();
      },
      { rootMargin: '160px' },
    );
    observer.observe(card);
    return () => observer.disconnect();
  }, [src]);

  const play = () => {
    const video = videoRef.current;
    if (!video || !video.src) return;
    if ((video.closest('[data-reels]') as HTMLElement | null)?.dataset.dragging === 'true') return;
    video.closest('[data-reels]')?.querySelectorAll('video').forEach((other) => {
      if (other !== video) other.pause();
    });
    setPlaying(true);
    video.play().catch(() => setPlaying(false));
  };

  const pause = () => {
    videoRef.current?.pause();
    setPlaying(false);
  };

  return (
    <div
      ref={cardRef}
      className="w-[78%] shrink-0 sm:w-[240px] lg:w-[calc((100%-3rem)/4)]"
      onMouseEnter={play}
      onMouseLeave={pause}
      onFocus={play}
      onBlur={pause}
      onClick={(event) => {
        if ((event.currentTarget.closest('[data-reels]') as HTMLElement | null)?.dataset.dragMoved === 'true') return;
        if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
        const video = videoRef.current;
        if (!video) return;
        if (video.paused) play();
        else pause();
      }}
    >
      <div className="rounded-[22px] shadow-[0_10px_22px_-8px_rgba(17,17,17,0.28)]">
      <div className="relative aspect-[9/16] overflow-hidden rounded-[22px] bg-neutral-100">
        <video
          ref={videoRef}
          src={near ? src : undefined}
          muted
          playsInline
          loop
          preload={near ? 'metadata' : 'none'}
          aria-label={`${name}, ${handle}`}
          className="absolute inset-0 w-full object-cover"
          style={{ height: '100%' }}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onLoadedData={(event) => {
            const video = event.currentTarget;
            if (video.paused && video.currentTime < 0.05) {
              try {
                video.currentTime = 0.05;
              } catch {
                // The browser has not made the file seekable yet.
              }
            }
          }}
        />
        <div
          className={`pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/45 to-transparent px-3 pb-3.5 pt-16 text-left transition-opacity duration-300 ${
            playing ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <p className="text-[14px] font-semibold leading-snug text-white">{name}</p>
          <a
            href={`https://www.instagram.com/${handle.slice(1)}/`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => {
              event.stopPropagation();
              trackSectionCta('click_instagram', 'homepage_voices_section', handle);
            }}
            className={`mt-0.5 inline-block text-[12px] font-medium text-white/90 hover:text-white ${
              playing ? 'pointer-events-auto' : 'pointer-events-none'
            }`}
          >
            {handle}
          </a>
        </div>
      </div>
      </div>
    </div>
  );
}

export default function HomeVoicesSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef({
    active: false,
    moved: false,
    animating: false,
    startX: 0,
    scrollLeft: 0,
    lastX: 0,
    lastTime: 0,
    velocity: 0,
    frame: 0,
  });
  const [enabled, setEnabled] = useState(false);
  const [active, setActive] = useState(0);

  const maxScrollOf = (scroller: HTMLElement) => Math.max(0, scroller.scrollWidth - scroller.clientWidth);

  const indexForScroll = (scroller: HTMLElement, scrollLeft = scroller.scrollLeft) => {
    const last = REELS.length - 1;
    const max = maxScrollOf(scroller);
    if (last <= 0 || max <= 1) return 0;
    const clamped = Math.min(max, Math.max(0, scrollLeft));
    if (clamped >= max - 2) return last;
    return Math.round((clamped / max) * last);
  };

  const scrollForIndex = (scroller: HTMLElement, index: number) => {
    const last = REELS.length - 1;
    if (last <= 0) return 0;
    return (index / last) * maxScrollOf(scroller);
  };

  const syncActive = () => {
    const scroller = scrollerRef.current;
    if (!scroller || dragRef.current.active || dragRef.current.animating) return;
    setActive(indexForScroll(scroller));
  };

  const animateTo = (left: number, index: number) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    cancelAnimationFrame(dragRef.current.frame);
    const start = scroller.scrollLeft;
    const change = left - start;
    if (Math.abs(change) < 1) {
      scroller.scrollLeft = left;
      dragRef.current.animating = false;
      scroller.style.scrollBehavior = '';
      setActive(index);
      return;
    }

    const duration = 480;
    const startTime = performance.now();
    dragRef.current.animating = true;
    scroller.style.scrollBehavior = 'auto';

    const step = (now: number) => {
      const progress = Math.min(1, (now - startTime) / duration);
      const eased = 1 - (1 - progress) ** 3;
      scroller.scrollLeft = start + change * eased;
      if (progress < 1) {
        dragRef.current.frame = requestAnimationFrame(step);
        return;
      }
      dragRef.current.animating = false;
      scroller.style.scrollBehavior = '';
      setActive(index);
    };

    dragRef.current.frame = requestAnimationFrame(step);
  };

  const goTo = (index: number) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    animateTo(scrollForIndex(scroller, index), index);
  };

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setEnabled(true);
        observer.disconnect();
      },
      { rootMargin: '0px' },
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const scroller = scrollerRef.current;
    if (!scroller) return;
    cancelAnimationFrame(dragRef.current.frame);
    dragRef.current = {
      active: true,
      moved: false,
      animating: false,
      startX: event.clientX,
      scrollLeft: scroller.scrollLeft,
      lastX: event.clientX,
      lastTime: performance.now(),
      velocity: 0,
      frame: 0,
    };
    scroller.dataset.dragging = 'true';
    delete scroller.dataset.dragMoved;
    scroller.style.scrollBehavior = 'auto';
    scroller.style.cursor = 'grabbing';
    if (scroller.setPointerCapture) {
      try {
        scroller.setPointerCapture(event.pointerId);
      } catch {
        // Pointer capture is unavailable for this event. Drag still tracks move events on the row.
      }
    }
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current.active) return;
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const now = performance.now();
    const delta = event.clientX - dragRef.current.startX;
    const stepX = event.clientX - dragRef.current.lastX;
    const stepTime = Math.max(now - dragRef.current.lastTime, 8);
    dragRef.current.velocity = stepX / stepTime;
    dragRef.current.lastX = event.clientX;
    dragRef.current.lastTime = now;
    if (Math.abs(delta) > 3) dragRef.current.moved = true;
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    scroller.scrollLeft = Math.min(maxScroll, Math.max(0, dragRef.current.scrollLeft - delta));
  };

  const endDrag = (event: PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current.active) return;
    const moved = dragRef.current.moved;
    dragRef.current.active = false;
    cancelAnimationFrame(dragRef.current.frame);
    const scroller = scrollerRef.current;
    if (scroller) {
      delete scroller.dataset.dragging;
      if (moved) scroller.dataset.dragMoved = 'true';
      scroller.style.cursor = '';
      if (scroller.hasPointerCapture(event.pointerId)) {
        scroller.releasePointerCapture(event.pointerId);
      }
    }
    if (!moved || !scroller) {
      if (scroller) scroller.style.scrollBehavior = '';
      return;
    }

    const maxScroll = maxScrollOf(scroller);
    const projected = Math.min(maxScroll, Math.max(0, scroller.scrollLeft - dragRef.current.velocity * 180));
    const index = indexForScroll(scroller, projected);
    animateTo(scrollForIndex(scroller, index), index);
  };

  return (
    <section ref={sectionRef} className="bg-white py-16 font-sans md:py-20">
      <div className="mx-auto w-full max-w-[1120px] px-5 md:px-8">
        <div className="text-center">
          <p className="text-[13px] font-semibold uppercase tracking-[2px] text-black">Real voices</p>
          <h2 className="mt-3 text-[clamp(22px,6.2vw,28px)] font-bold leading-tight text-black md:text-[40px]">
            <span className="block whitespace-nowrap">People you trust,</span>
            <span className="block whitespace-nowrap">talking about love that lasts</span>
          </h2>
          <p className="mx-auto mt-4 max-w-[36ch] text-[15px] leading-relaxed text-neutral-600 md:mt-5 md:max-w-[52ch] md:text-[16px]">
            Creators are sharing why they trust us to find the right match. Watch their stories and see what makes us
            different.
          </p>
        </div>

        <div className="-mx-5 mt-8 md:-mx-3 md:mt-12">
          <div
            ref={scrollerRef}
            data-reels
            onScroll={syncActive}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onClickCapture={(event) => {
              const scroller = scrollerRef.current;
              if (!scroller || scroller.dataset.dragMoved !== 'true') return;
              event.preventDefault();
              event.stopPropagation();
              delete scroller.dataset.dragMoved;
            }}
            className="scrollbar-hide flex cursor-grab touch-pan-y gap-4 overflow-x-auto px-5 py-7 md:px-3"
          >
            {REELS.map((reel) => (
              <ReelCard key={reel.file} src={enabled ? reelSrc(reel.file) : undefined} name={reel.name} handle={reel.handle} />
            ))}
          </div>
        </div>

        <div className="mt-1 flex items-center justify-center gap-2" role="tablist" aria-label="Creator reels">
          {REELS.map((reel, index) => (
            <button
              key={reel.file}
              type="button"
              role="tab"
              aria-selected={index === active}
              aria-label={`Show ${reel.name}`}
              onClick={() => goTo(index)}
              className={`h-2 rounded-full transition-all ${
                index === active ? 'w-2.5 bg-[#6B39FF]' : 'w-2 bg-[#ddd6f0]'
              }`}
            />
          ))}
        </div>

        <div className="mt-8 text-center md:mt-10">
          <Link
            href="/register"
            onClick={() =>
              trackSectionCta('click_register', 'homepage_voices_section', 'Register Now in 2 minutes')
            }
            className="inline-flex w-full max-w-[420px] items-center justify-center rounded-full bg-primary-600 px-8 py-3.5 text-[16px] font-semibold text-white transition-colors hover:bg-primary-700 md:w-auto"
          >
            Register Now in 2 minutes
          </Link>
        </div>
      </div>
    </section>
  );
}
